import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Topbar } from '../components/Topbar'
import {
  Plus,
  Edit,
  Trash2,
  Loader2,
  Send,
  Bell,
  X,
  Save,
  MessageSquare,
  Hash,
  Link as LinkIcon,
  CheckCircle2,
  XCircle,
  Webhook,
} from 'lucide-react'
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api'

type ChannelType = 'slack' | 'discord' | 'teams' | 'webhook' | 'telegram'

interface Channel {
  id: string
  name: string
  type: ChannelType
  url: string
  secret: string | null
  isActive: boolean
  events: string[]
  lastStatus: 'success' | 'fail' | null
  lastError: string | null
  lastSentAt: string | null
  createdAt: string | null
  updatedAt: string | null
}

interface ListResp {
  items: Channel[]
  supportedEvents: string[]
}

interface FormState {
  name: string
  type: ChannelType
  url: string
  secret: string
  isActive: boolean
  events: string[]
}

const emptyForm: FormState = {
  name: '',
  type: 'slack',
  url: '',
  secret: '',
  isActive: true,
  events: ['order.created', 'order.paid', 'payment.failed'],
}

const TYPE_META: Record<ChannelType, { label: string; cls: string; icon: typeof MessageSquare }> = {
  slack: {
    label: 'Slack',
    cls: 'bg-sky-100 text-sky-700 border border-sky-200',
    icon: Hash,
  },
  discord: {
    label: 'Discord',
    cls: 'bg-violet-100 text-violet-700 border border-violet-200',
    icon: MessageSquare,
  },
  teams: {
    label: 'Teams',
    cls: 'bg-indigo-100 text-indigo-700 border border-indigo-200',
    icon: MessageSquare,
  },
  telegram: {
    label: 'Telegram',
    cls: 'bg-cyan-100 text-cyan-700 border border-cyan-200',
    icon: Send,
  },
  webhook: {
    label: 'Webhook',
    cls: 'bg-ink-100 text-ink-700 border border-ink-200',
    icon: Webhook,
  },
}

const EVENT_LABELS: Record<string, string> = {
  'order.created': 'Nuevo pedido',
  'order.paid': 'Pedido pagado',
  'order.cancelled': 'Pedido cancelado',
  'payment.failed': 'Pago fallido',
  low_stock: 'Stock bajo',
  new_review_negative: 'Reseña negativa',
  'system.error': 'Error de sistema',
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

export function NotifChannels() {
  const qc = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Channel | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [formError, setFormError] = useState<string | null>(null)
  const [testingId, setTestingId] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<{
    id: string
    ok: boolean
    msg: string
  } | null>(null)

  const { data, isLoading, isFetching } = useQuery<ListResp>({
    queryKey: ['notif-channels'],
    queryFn: () => apiGet('/api/admin/notif-channels'),
  })

  const supportedEvents = data?.supportedEvents ?? [
    'order.created',
    'order.paid',
    'order.cancelled',
    'payment.failed',
    'low_stock',
    'new_review_negative',
    'system.error',
  ]

  const saveMutation = useMutation({
    mutationFn: async (payload: { id: string | null; data: unknown }) => {
      if (payload.id) {
        return apiPut(`/api/admin/notif-channels/${payload.id}`, payload.data)
      }
      return apiPost('/api/admin/notif-channels', payload.data)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notif-channels'] })
      closeModal()
    },
    onError: (e: Error) => setFormError(e.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/admin/notif-channels/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notif-channels'] }),
    onError: (e: Error) => alert(e.message),
  })

  const toggleActiveMutation = useMutation({
    mutationFn: (c: Channel) =>
      apiPut(`/api/admin/notif-channels/${c.id}`, { is_active: !c.isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notif-channels'] }),
    onError: (e: Error) => alert(e.message),
  })

  function openCreate() {
    setEditing(null)
    setForm(emptyForm)
    setFormError(null)
    setModalOpen(true)
  }

  function openEdit(c: Channel) {
    setEditing(c)
    setForm({
      name: c.name,
      type: c.type,
      url: c.url,
      secret: c.secret ?? '',
      isActive: c.isActive,
      events: c.events,
    })
    setFormError(null)
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
    setEditing(null)
    setForm(emptyForm)
    setFormError(null)
  }

  function toggleEvent(ev: string) {
    setForm((f) =>
      f.events.includes(ev)
        ? { ...f, events: f.events.filter((e) => e !== ev) }
        : { ...f, events: [...f.events, ev] },
    )
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)
    if (!form.name.trim() || !form.url.trim()) {
      setFormError('Nombre y URL son obligatorios')
      return
    }
    if (form.events.length === 0) {
      setFormError('Selecciona al menos un evento')
      return
    }
    const payload = {
      name: form.name.trim(),
      type: form.type,
      url: form.url.trim(),
      secret: form.secret.trim() || null,
      is_active: form.isActive,
      events: form.events,
    }
    saveMutation.mutate({ id: editing?.id ?? null, data: payload })
  }

  function onDelete(c: Channel) {
    if (!confirm(`¿Eliminar el canal "${c.name}"?`)) return
    deleteMutation.mutate(c.id)
  }

  async function onTest(c: Channel) {
    setTestingId(c.id)
    setTestResult(null)
    try {
      const r = await apiPost<{ ok: boolean; status?: number; error?: string }>(
        `/api/admin/notif-channels/${c.id}/test`,
      )
      setTestResult({
        id: c.id,
        ok: r.ok,
        msg: r.ok ? `Enviado (HTTP ${r.status ?? '200'})` : r.error || 'Falló el envío',
      })
      qc.invalidateQueries({ queryKey: ['notif-channels'] })
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setTestResult({ id: c.id, ok: false, msg })
    } finally {
      setTestingId(null)
      setTimeout(() => setTestResult(null), 6000)
    }
  }

  const items = data?.items ?? []

  return (
    <main className="min-h-screen bg-ink-50">
      <Topbar
        title="Notificaciones push"
        subtitle={
          data
            ? `${items.length.toLocaleString('es-MX')} canal${items.length === 1 ? '' : 'es'} configurados`
            : 'Cargando…'
        }
        actions={
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Plus className="size-3.5" /> Nuevo canal
          </button>
        }
      />
      <div className="p-6">
        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando canales…</p>
            </div>
          )}

          {data && (
            <div className="relative overflow-x-auto">
              {isFetching && (
                <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                  <Loader2 className="size-4 animate-spin text-brand-600" />
                </div>
              )}
              <table className="w-full text-sm">
                <thead className="bg-ink-50">
                  <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                    <th className="px-4 py-3 font-medium">Canal</th>
                    <th className="px-4 py-3 font-medium">Tipo</th>
                    <th className="px-4 py-3 font-medium">Eventos</th>
                    <th className="px-4 py-3 font-medium">Último envío</th>
                    <th className="px-4 py-3 font-medium">Activo</th>
                    <th className="w-44 px-4 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-16 text-center text-ink-500">
                        <Bell className="mx-auto mb-3 size-8 opacity-40" />
                        No hay canales configurados. Agrega uno para recibir alertas en
                        Slack, Discord o tu webhook favorito.
                      </td>
                    </tr>
                  )}
                  {items.map((c) => {
                    const meta = TYPE_META[c.type] ?? TYPE_META.webhook
                    const Icon = meta.icon
                    const showResult = testResult?.id === c.id
                    return (
                      <tr
                        key={c.id}
                        className="border-t border-ink-100 transition-colors hover:bg-ink-50"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-600">
                              <Icon className="size-4" />
                            </div>
                            <div>
                              <div className="font-medium text-ink-900">{c.name}</div>
                              <div className="flex items-center gap-1 text-xs text-ink-500">
                                <LinkIcon className="size-3" />
                                <span className="max-w-xs truncate">{c.url}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${meta.cls}`}
                          >
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex max-w-xs flex-wrap gap-1">
                            {c.events.length === 0 && (
                              <span className="text-xs text-ink-400">—</span>
                            )}
                            {c.events.slice(0, 3).map((ev) => (
                              <span
                                key={ev}
                                className="rounded bg-ink-100 px-1.5 py-0.5 text-[10px] font-medium text-ink-700"
                                title={ev}
                              >
                                {EVENT_LABELS[ev] ?? ev}
                              </span>
                            ))}
                            {c.events.length > 3 && (
                              <span className="rounded bg-ink-100 px-1.5 py-0.5 text-[10px] font-medium text-ink-500">
                                +{c.events.length - 3}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {c.lastSentAt ? (
                            <div className="flex flex-col">
                              <span className="text-xs text-ink-700">
                                {fmtDateTime(c.lastSentAt)}
                              </span>
                              {c.lastStatus === 'success' && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-700">
                                  <CheckCircle2 className="size-3" /> OK
                                </span>
                              )}
                              {c.lastStatus === 'fail' && (
                                <span
                                  className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700"
                                  title={c.lastError ?? ''}
                                >
                                  <XCircle className="size-3" /> Falló
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-ink-400">Nunca</span>
                          )}
                          {showResult && (
                            <div
                              className={`mt-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${
                                testResult!.ok
                                  ? 'bg-green-50 text-green-700'
                                  : 'bg-red-50 text-red-700'
                              }`}
                            >
                              {testResult!.msg}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleActiveMutation.mutate(c)}
                            disabled={toggleActiveMutation.isPending}
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition ${
                              c.isActive
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-ink-100 text-ink-500 hover:bg-ink-200'
                            }`}
                          >
                            {c.isActive ? 'Activo' : 'Inactivo'}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => onTest(c)}
                              disabled={testingId === c.id}
                              className="inline-flex items-center gap-1 rounded-md border border-ink-200 px-2 py-1 text-xs font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50"
                              title="Enviar mensaje de prueba"
                            >
                              {testingId === c.id ? (
                                <Loader2 className="size-3 animate-spin" />
                              ) : (
                                <Send className="size-3" />
                              )}
                              Test
                            </button>
                            <button
                              onClick={() => openEdit(c)}
                              className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                              title="Editar"
                            >
                              <Edit className="size-4" />
                            </button>
                            <button
                              onClick={() => onDelete(c)}
                              disabled={deleteMutation.isPending}
                              className="rounded-md p-1.5 text-ink-500 hover:bg-red-100 hover:text-red-700"
                              title="Eliminar"
                            >
                              <Trash2 className="size-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-6">
          <form
            onSubmit={onSubmit}
            className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-ink-900">
                {editing ? 'Editar canal' : 'Nuevo canal de notificaciones'}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-md p-1 text-ink-500 hover:bg-ink-100"
              >
                <X className="size-4" />
              </button>
            </div>
            {formError && (
              <div className="mb-3 rounded-lg bg-red-50 p-2 text-xs text-red-700">
                {formError}
              </div>
            )}
            <div className="grid gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Nombre" required>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className={inputCls}
                    maxLength={150}
                    placeholder="ej. Slack ventas"
                    required
                  />
                </Field>
                <Field label="Tipo" required>
                  <select
                    value={form.type}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, type: e.target.value as ChannelType }))
                    }
                    className={inputCls}
                  >
                    <option value="slack">Slack</option>
                    <option value="discord">Discord</option>
                    <option value="teams">Microsoft Teams</option>
                    <option value="telegram">Telegram</option>
                    <option value="webhook">Webhook genérico</option>
                  </select>
                </Field>
              </div>
              <Field label="URL del webhook" required>
                <input
                  type="url"
                  value={form.url}
                  onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                  className={`${inputCls} font-mono text-xs`}
                  maxLength={2000}
                  placeholder="https://hooks.slack.com/services/..."
                  required
                />
              </Field>
              <Field
                label={
                  form.type === 'telegram'
                    ? 'chat_id (secreto)'
                    : form.type === 'webhook'
                    ? 'Secreto (X-Maz-Secret header)'
                    : 'Secreto (opcional)'
                }
              >
                <input
                  type="text"
                  value={form.secret}
                  onChange={(e) => setForm((f) => ({ ...f, secret: e.target.value }))}
                  className={`${inputCls} font-mono text-xs`}
                  maxLength={255}
                  placeholder="opcional"
                />
              </Field>
              <Field label="Eventos suscritos" required>
                <div className="grid gap-1.5 rounded-lg border border-ink-200 bg-ink-50 p-3 sm:grid-cols-2">
                  {supportedEvents.map((ev) => (
                    <label
                      key={ev}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-white"
                    >
                      <input
                        type="checkbox"
                        checked={form.events.includes(ev)}
                        onChange={() => toggleEvent(ev)}
                      />
                      <span className="font-medium text-ink-800">
                        {EVENT_LABELS[ev] ?? ev}
                      </span>
                      <span className="ml-auto font-mono text-[10px] text-ink-400">
                        {ev}
                      </span>
                    </label>
                  ))}
                </div>
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, isActive: e.target.checked }))
                  }
                />
                <span>Canal activo</span>
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saveMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {saveMutation.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Save className="size-3.5" />
                )}
                Guardar
              </button>
            </div>
          </form>
        </div>
      )}
    </main>
  )
}

export default NotifChannels

const inputCls =
  'w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 outline-none transition-colors focus:border-brand-500'

function Field({
  label,
  required,
  children,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-ink-700">
        {label}
        {required && <span className="ml-0.5 text-red-600">*</span>}
      </span>
      {children}
    </label>
  )
}
